import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const EstudioEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [estudio, setEstudio] = useState({
    nombre: '',
    descripcion: '',
    precio: 0,
    precio_urgente: 0,
    tiempo_entrega: '',
    categoria: '',
    preparacion: '',
    urgente: false,
    activo: true
  });
  const [detallesEspecies, setDetallesEspecies] = useState([]);

  useEffect(() => {
    if (id !== 'nuevo') {
      loadEstudio();
    } else {
      setLoading(false);
    }
  }, [id]);

  const loadEstudio = async () => {
    try {
      const response = await api.get(`/estudios/${id}`);
      setEstudio(response.data);
      setDetallesEspecies(response.data.detalles_especies || []);
      setLoading(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo cargar el estudio',
        variant: 'destructive',
      });
      navigate('/admin');
    }
  };

  const actualizarEstudio = (campo, valor) => {
    setEstudio({ ...estudio, [campo]: valor });
  };

  const agregarEspecie = () => {
    setDetallesEspecies([
      ...detallesEspecies,
      {
        especie: '',
        cantidad_sangre: '',
        color_tubo: '',
        refrigeracion: false,
        instrucciones_adicionales: ''
      }
    ]);
  };

  const actualizarEspecie = (index, campo, valor) => {
    const nuevosDetalles = [...detallesEspecies];
    nuevosDetalles[index][campo] = valor;
    setDetallesEspecies(nuevosDetalles);
  };

  const eliminarEspecie = (index) => {
    const nuevosDetalles = detallesEspecies.filter((_, i) => i !== index);
    setDetallesEspecies(nuevosDetalles);
  };

  const guardarCambios = async () => {
    try {
      if (id === 'nuevo') {
        // Crear nuevo estudio
        await api.post('/admin/estudios', {
          ...estudio,
          detalles_especies: detallesEspecies
        });
        toast({
          title: '✅ Estudio creado',
          description: 'El nuevo estudio se creó correctamente',
        });
      } else {
        // Actualizar estudio existente
        await api.put(`/admin/estudios/${id}`, {
          ...estudio,
          detalles_especies: detallesEspecies
        });
        toast({
          title: '✅ Cambios guardados',
          description: 'Los detalles del estudio se actualizaron correctamente',
        });
      }
      navigate('/admin');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron guardar los cambios',
        variant: 'destructive',
      });
    }
  };

  const eliminarEstudio = async () => {
    if (!window.confirm('¿Estás seguro de eliminar este estudio? Esta acción no se puede deshacer.')) {
      return;
    }
    try {
      await api.delete(`/admin/estudios/${id}`);
      toast({
        title: 'Estudio eliminado',
        description: 'El estudio se eliminó correctamente',
      });
      navigate('/admin');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el estudio',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/admin" className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-4">
            <ArrowLeft className="h-5 w-5" />
            Volver al Panel
          </Link>
          <h1 className="text-3xl font-bold">
            {id === 'nuevo' ? 'Nuevo Estudio' : estudio?.nombre}
          </h1>
          <p className="text-lg opacity-90">
            {id === 'nuevo' ? 'Crear estudio y agregar detalles' : 'Editor de detalles por especie'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Información básica */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-[#1e3a8a]">📄 Información Básica</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nombre del Estudio *</label>
                <Input
                  value={estudio.nombre}
                  onChange={(e) => actualizarEstudio('nombre', e.target.value)}
                  placeholder="Ej: Hemograma Completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Categoría *</label>
                <select
                  value={estudio.categoria}
                  onChange={(e) => actualizarEstudio('categoria', e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Selecciona...</option>
                  <option value="Hematología">Hematología</option>
                  <option value="Bioquímica">Bioquímica</option>
                  <option value="Electrolitos">Electrolitos</option>
                  <option value="Orina">Orina</option>
                  <option value="Parasitología">Parasitología</option>
                  <option value="Paquetes">Paquetes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Precio (MXN) *</label>
                <Input
                  type="number"
                  value={estudio.precio}
                  onChange={(e) => actualizarEstudio('precio', parseFloat(e.target.value))}
                  placeholder="155"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tiempo de Entrega *</label>
                <Input
                  value={estudio.tiempo_entrega}
                  onChange={(e) => actualizarEstudio('tiempo_entrega', e.target.value)}
                  placeholder="Ej: Mismo día, 24-48 horas"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Preparación *</label>
                <Input
                  value={estudio.preparacion}
                  onChange={(e) => actualizarEstudio('preparacion', e.target.value)}
                  placeholder="Ej: Ayuno de 8 horas"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">¿Es urgente?</label>
                <div className="flex items-center gap-6 mt-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={estudio.urgente}
                      onChange={(e) => actualizarEstudio('urgente', e.target.checked)}
                    />
                    <span>Servicio urgente disponible</span>
                  </label>
                </div>
              </div>

              {estudio.urgente && (
                <div>
                  <label className="block text-sm font-medium mb-2">Precio Urgente (MXN)</label>
                  <Input
                    type="number"
                    value={estudio.precio_urgente || 0}
                    onChange={(e) => actualizarEstudio('precio_urgente', parseFloat(e.target.value))}
                    placeholder="245"
                  />
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Descripción</label>
                <Textarea
                  value={estudio.descripcion}
                  onChange={(e) => actualizarEstudio('descripcion', e.target.value)}
                  placeholder="Descripción del estudio..."
                  rows={3}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detalles por especie */}
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-[#1e3a8a]">🐾 Detalles por Especie</CardTitle>
            <Button onClick={agregarEspecie} className="bg-[#84cc16] hover:bg-[#65a30d]">
              <Plus className="h-4 w-4 mr-2" />
              Agregar Especie
            </Button>
          </CardHeader>
          <CardContent>
            {detallesEspecies.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No hay detalles específicos por especie.</p>
                <p className="text-sm mt-2">Haz clic en "Agregar Especie" para comenzar.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {detallesEspecies.map((detalle, index) => (
                  <Card key={index} className="border-2 border-gray-200">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-[#1e40af]">
                          Especie #{index + 1}
                        </h3>
                        <Button
                          onClick={() => eliminarEspecie(index)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Tipo de Especie *
                          </label>
                          <select
                            value={detalle.especie}
                            onChange={(e) => actualizarEspecie(index, 'especie', e.target.value)}
                            className="w-full p-2 border rounded"
                          >
                            <option value="">Selecciona...</option>
                            <option value="aves">Aves</option>
                            <option value="perros_gatos">Perros y Gatos</option>
                            <option value="no_convencionales">Animales No Convencionales</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Cantidad de Sangre *
                          </label>
                          <Input
                            value={detalle.cantidad_sangre}
                            onChange={(e) => actualizarEspecie(index, 'cantidad_sangre', e.target.value)}
                            placeholder="Ej: 2-3 ml"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Color de Tubo *
                          </label>
                          <Input
                            value={detalle.color_tubo}
                            onChange={(e) => actualizarEspecie(index, 'color_tubo', e.target.value)}
                            placeholder="Ej: Morado (EDTA)"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Refrigeración
                          </label>
                          <div className="flex items-center gap-4 mt-3">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={detalle.refrigeracion === true}
                                onChange={() => actualizarEspecie(index, 'refrigeracion', true)}
                              />
                              <span>Sí requiere</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={detalle.refrigeracion === false}
                                onChange={() => actualizarEspecie(index, 'refrigeracion', false)}
                              />
                              <span>No requiere</span>
                            </label>
                          </div>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2">
                            Instrucciones Adicionales
                          </label>
                          <Textarea
                            value={detalle.instrucciones_adicionales}
                            onChange={(e) => actualizarEspecie(index, 'instrucciones_adicionales', e.target.value)}
                            placeholder="Ej: Evitar hemólisis, ayuno de 8 horas..."
                            rows={3}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Botones de acción */}
        <div className="flex justify-between gap-4">
          <div>
            {id !== 'nuevo' && (
              <Button
                onClick={eliminarEstudio}
                variant="destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar Estudio
              </Button>
            )}
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
            >
              Cancelar
            </Button>
            <Button
              onClick={guardarCambios}
              className="bg-[#1e40af] hover:bg-[#1e3a8a]"
            >
              <Save className="h-4 w-4 mr-2" />
              {id === 'nuevo' ? 'Crear Estudio' : 'Guardar Cambios'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstudioEditor;
